import React, { useState } from 'react'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@shared/components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@shared/components/ui/alert-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { toast } from '@shared/hooks/use-toast'
import { X, CreditCard, RefreshCw, AlertTriangle } from 'lucide-react'
import { apiUrl } from '@/lib/api'

interface ActionButtonsSectionProps {
  subscription: {
    id: string
    status: string
    billing_cycle: string
    plan_name?: string
  }
  plans: Array<{ id: string; name: string }>
  onSubscriptionUpdated: () => void
}

export const ActionButtonsSection: React.FC<ActionButtonsSectionProps> = ({
  subscription,
  plans,
  onSubscriptionUpdated
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showRefundDialog, setShowRefundDialog] = useState(false)
  const [showChangePlanDialog, setShowChangePlanDialog] = useState(false)
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true)
  const [refundAmount, setRefundAmount] = useState('')
  const [newPlanId, setNewPlanId] = useState('')
  const [loading, setLoading] = useState(false)


  const handleCancel = async () => {
    setLoading(true)
    try {
      // Get fresh token
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        console.error('No authentication token available')
        return
      }

      const requestHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      const response = await fetch(apiUrl(`/api/admin/subscriptions/${subscription.id}/cancel`), {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify({ atPeriodEnd: cancelAtPeriodEnd })
      })
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Subscription canceled successfully' })
        onSubscriptionUpdated()
        setShowCancelDialog(false)
      } else {
        const error = await response.json().catch(() => ({}))
        toast({ title: 'Error', description: error.error || 'Failed to cancel subscription', variant: 'destructive' })
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to cancel subscription', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleRefund = async () => {
    if (!refundAmount || isNaN(Number(refundAmount))) {
      toast({ title: 'Error', description: 'Please enter a valid refund amount', variant: 'destructive' })
      return
    }

    setLoading(true)
    try {
      // Get fresh token
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        console.error('No authentication token available')
        return
      }

      const requestHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      const response = await fetch(apiUrl(`/api/admin/subscriptions/${subscription.id}/refund`), {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify({ amount: Number(refundAmount) * 100 }) // Convert to cents
      })
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Refund processed successfully' })
        onSubscriptionUpdated()
        setShowRefundDialog(false)
        setRefundAmount('')
      } else {
        const error = await response.json().catch(() => ({}))
        toast({ title: 'Error', description: error.error || 'Failed to process refund', variant: 'destructive' })
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to process refund', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleChangePlan = async () => {
    if (!newPlanId) {
      toast({ title: 'Error', description: 'Please select a plan', variant: 'destructive' })
      return
    }

    setLoading(true)
    try {
      // Get fresh token
      const { auth } = await import('@/lib/firebase')
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        console.error('No authentication token available')
        return
      }

      const requestHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      const response = await fetch(apiUrl(`/api/admin/subscriptions/${subscription.id}/change-plan`), {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify({ 
          newPlanId, 
          billingCycle: subscription.billing_cycle || 'month' 
        })
      })
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Plan changed successfully' })
        onSubscriptionUpdated()
        setShowChangePlanDialog(false)
        setNewPlanId('')
      } else {
        const error = await response.json().catch(() => ({}))
        if (error.requiresManualProcess) {
          toast({ 
            title: 'Manual Process Required', 
            description: error.message || 'Billing cycle changes require manual cancellation and recreation',
            variant: 'destructive' 
          })
        } else {
          toast({ title: 'Error', description: error.error || 'Failed to change plan', variant: 'destructive' })
        }
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to change plan', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const isActive = subscription.status === 'active' || subscription.status === 'trialing'
  const isCanceled = subscription.status === 'canceled'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        {/* Cancel Subscription */}
        {isActive && (
          <Button
            variant="destructive"
            onClick={() => setShowCancelDialog(true)}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Cancel Subscription
          </Button>
        )}

        {/* Refund */}
        {isActive && (
          <Button
            variant="outline"
            onClick={() => setShowRefundDialog(true)}
            className="flex items-center gap-2"
          >
            <CreditCard className="h-4 w-4" />
            Process Refund
          </Button>
        )}

        {/* Change Plan */}
        {isActive && (
          <Button
            variant="outline"
            onClick={() => setShowChangePlanDialog(true)}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Change Plan
          </Button>
        )}

        {/* Resume Subscription */}
        {isCanceled && (
          <Button
            variant="default"
            onClick={() => setShowChangePlanDialog(true)}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reactivate Subscription
          </Button>
        )}
      </div>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              Choose how you want to cancel this subscription.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Cancelation Type</Label>
              <Select value={cancelAtPeriodEnd ? 'period' : 'immediate'} onValueChange={(v) => setCancelAtPeriodEnd(v === 'period')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="period">Cancel at period end (recommended)</SelectItem>
                  <SelectItem value="immediate">Cancel immediately</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cancelAtPeriodEnd ? (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                The subscription will remain active until the current billing period ends. The customer will retain access until then.
              </div>
            ) : (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                <strong>Warning:</strong> This will immediately cancel the subscription and suspend access. A prorated refund may be issued.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={loading}>
              {loading ? 'Canceling...' : 'Confirm Cancelation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Process Refund
            </DialogTitle>
            <DialogDescription>
              Enter the amount to refund to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="refund-amount">Refund Amount (USD)</Label>
              <Input
                id="refund-amount"
                type="number"
                step="0.01"
                min="0"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefundDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRefund} disabled={loading || !refundAmount}>
              {loading ? 'Processing...' : 'Process Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={showChangePlanDialog} onOpenChange={setShowChangePlanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              {isCanceled ? 'Reactivate Subscription' : 'Change Plan'}
            </DialogTitle>
            <DialogDescription>
              {isCanceled 
                ? 'Select a plan to reactivate this subscription.'
                : 'Select a new plan for this subscription. The current billing cycle will be maintained.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Plan</Label>
              <Select value={newPlanId} onValueChange={setNewPlanId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map(plan => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} ({subscription.billing_cycle === 'annual' ? 'Annual' : 'Monthly'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isCanceled && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                Plan changes will maintain the current billing cycle ({subscription.billing_cycle || 'monthly'}).
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePlanDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangePlan} disabled={loading || !newPlanId}>
              {loading ? 'Processing...' : (isCanceled ? 'Reactivate' : 'Change Plan')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

